import React, { useState, useCallback } from 'react';
import {
  X,
  Upload,
  FileText,
  Settings,
  Activity,
  Shield,
  UserCheck,
  Radar,
  Plug,
  Calendar,
  AlertTriangle,
  Link2
} from 'lucide-react';
import {
  EVIDENCE_TYPES,
  EVIDENCE_STATUS,
  calculateExpirationDate
} from '../../config/cisaEvidenceModel';
import './EvidenceUploadModal.css';

const EVIDENCE_TYPE_ICONS = {
  POLICY_EXPORT: FileText,
  CONFIG_SNAPSHOT: Settings,
  LOG_COVERAGE: Activity,
  GOVERNANCE_ARTIFACT: Shield,
  MANUAL_ATTESTATION: UserCheck,
  AUTOMATED_SCAN: Radar,
  INTEGRATION_API: Plug
};

/**
 * EvidenceUploadModal - Modal for adding evidence to support maturity assessments
 *
 * Features:
 * - Evidence type selector
 * - Source description
 * - File upload dropzone (optional)
 * - Expiration date (auto-calculated from type)
 * - Link to function and product
 */
function EvidenceUploadModal({
  isOpen,
  onClose,
  onSubmit,
  functionCode,
  functionName,
  relatedCheck,
  adoptedProducts = []
}) {
  const [evidenceType, setEvidenceType] = useState('POLICY_EXPORT');
  const [source, setSource] = useState(relatedCheck?.source || '');
  const [notes, setNotes] = useState('');
  const [linkedProduct, setLinkedProduct] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');

  const selectedType = EVIDENCE_TYPES[evidenceType];
  const Icon = EVIDENCE_TYPE_ICONS[evidenceType] || FileText;
  const expirationDate = calculateExpirationDate(evidenceType);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      setFileName(file.name);
      // In a real implementation, this would upload to cloud storage
      // For now, we'll use a placeholder URL
      setAttachmentUrl(`file://${file.name}`);
    }
  }, []);

  const handleFileSelect = useCallback((e) => {
    const files = e.target.files;
    if (files.length > 0) {
      const file = files[0];
      setFileName(file.name);
      setAttachmentUrl(`file://${file.name}`);
    }
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    const evidence = {
      type: evidenceType,
      source: source.trim(),
      notes: notes.trim(),
      status: 'VERIFIED',
      productId: linkedProduct || null,
      attachmentUrl: attachmentUrl || null,
      relatedCheckId: relatedCheck?.id || null
    };

    onSubmit?.(evidence);
    onClose?.();
  }, [evidenceType, source, notes, linkedProduct, attachmentUrl, relatedCheck, onSubmit, onClose]);

  if (!isOpen) return null;

  return (
    <div className="eum-overlay" onClick={onClose}>
      <div className="eum-modal" onClick={(e) => e.stopPropagation()}>
        <div className="eum-header">
          <div className="eum-header-content">
            <h3>Add Evidence</h3>
            <span className="eum-function-badge">{functionName}</span>
          </div>
          <button className="eum-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="eum-body">
            {/* Related Check Info */}
            {relatedCheck && (
              <div className="eum-related-check">
                <div className="eum-related-label">
                  <Link2 size={14} />
                  Linked to check
                </div>
                <div className="eum-related-question">{relatedCheck.question}</div>
              </div>
            )}

            {/* Evidence Type Selector */}
            <div className="eum-field">
              <label className="eum-label">Evidence Type</label>
              <div className="eum-type-grid">
                {Object.entries(EVIDENCE_TYPES).map(([key, type]) => {
                  const TypeIcon = EVIDENCE_TYPE_ICONS[key] || FileText;
                  const isSelected = evidenceType === key;

                  return (
                    <button
                      key={key}
                      type="button"
                      className={`eum-type-btn ${isSelected ? 'eum-type-selected' : ''}`}
                      onClick={() => setEvidenceType(key)}
                      style={{ '--type-color': type.color }}
                    >
                      <TypeIcon size={18} />
                      <span className="eum-type-label">{type.label}</span>
                      <span className="eum-type-expiry">{type.expiryDays}d</span>
                    </button>
                  );
                })}
              </div>
              <div className="eum-type-description">
                <Icon size={14} style={{ color: selectedType.color }} />
                {selectedType.description}
              </div>
            </div>

            {/* Source Description */}
            <div className="eum-field">
              <label className="eum-label">Source Description</label>
              <input
                type="text"
                className="eum-input"
                placeholder="e.g., Okta admin console > Security > Policies"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                required
              />
            </div>

            {/* File Upload Dropzone */}
            <div className="eum-field">
              <label className="eum-label">Attachment (Optional)</label>
              <div
                className={`eum-dropzone ${isDragging ? 'eum-dropzone-active' : ''} ${fileName ? 'eum-dropzone-has-file' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {fileName ? (
                  <div className="eum-file-info">
                    <FileText size={24} />
                    <span className="eum-file-name">{fileName}</span>
                    <button
                      type="button"
                      className="eum-file-remove"
                      onClick={() => {
                        setFileName('');
                        setAttachmentUrl('');
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload size={24} />
                    <span>Drag & drop file here or</span>
                    <label className="eum-file-btn">
                      Browse
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.csv,.json,.xml"
                        onChange={handleFileSelect}
                        hidden
                      />
                    </label>
                  </>
                )}
              </div>
            </div>

            {/* Linked Product */}
            {adoptedProducts.length > 0 && (
              <div className="eum-field">
                <label className="eum-label">Link to Product (Optional)</label>
                <select
                  className="eum-select"
                  value={linkedProduct}
                  onChange={(e) => setLinkedProduct(e.target.value)}
                >
                  <option value="">-- Select a product --</option>
                  {adoptedProducts.map((p) => (
                    <option key={p.productName} value={p.productName}>
                      {p.productName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Notes */}
            <div className="eum-field">
              <label className="eum-label">Notes (Optional)</label>
              <textarea
                className="eum-textarea"
                placeholder="Additional context or justification..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>

            {/* Expiration Warning */}
            <div className="eum-expiration-notice">
              <Calendar size={14} />
              <span>
                This evidence will expire on{' '}
                <strong>{new Date(expirationDate).toLocaleDateString()}</strong>{' '}
                ({selectedType.expiryDays} days from now)
              </span>
            </div>
          </div>

          <div className="eum-footer">
            <button type="button" className="eum-btn eum-btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="eum-btn eum-btn-submit"
              disabled={!source.trim()}
            >
              <Upload size={16} />
              Add Evidence
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EvidenceUploadModal;
